/* global SideDrawer */

// ── per‑product Liquid templates live in the card alongside each button ──
window.quickAddUpsellTpl = (data, opener) => {
  const card = opener.closest(".card__quick-add");
  const tpl = card?.querySelector(".quick-add-upsell-template");
  return tpl ? tpl.innerHTML : "";
};
window.quickAddCrossTpl = (data, opener) => {
  const card = opener.closest(".card__quick-add");
  const tpl = card?.querySelector(".quick-add-cross-template");
  return tpl ? tpl.innerHTML : "";
};

if (!customElements.get("quick-add-drawer")) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();

      // we’re already the <quick-add-drawer> instance:
      this.content = this.querySelector(".js-product-details");
      this.footer = this.querySelector(".drawer__footer");
      this.form = this.querySelector("product-form");
      this.notification = this.querySelector(".js-added-to-cart");
      this.backBtn = this.querySelector(".drawer__back-btn");
      this.openCartDrawerLinks = this.querySelectorAll(".js-open-cart-drawer");
      this.cartDrawer = document.querySelector("cart-drawer");
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler =
        this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener("click", this.documentClickHandler);
      this.addEventListener(
        "on:variant:change",
        this.handleVariantChange.bind(this)
      );

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener("click", this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll(".js-quick-add").forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ("MutationObserver" in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll(".js-quick-add").forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll(".js-quick-add").forEach((button) => {
                    button.removeEventListener(
                      "mouseenter",
                      this.quickAddButtonMouseEnterHandler
                    );
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, {
            childList: true,
            subtree: true,
          });
        }
      }
    }

    /** return the <quick-add-drawer> that encloses a given opener button */
    static drawerFor(opener) {
      return (
        opener.closest("product-card")?.querySelector("quick-add-drawer") ||
        document.querySelector("quick-add-drawer")
      );
    }

    static safeJson(raw) {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }

    disconnectedCallback() {
      document.removeEventListener("click", this.documentClickHandler);
      document.querySelectorAll(".js-quick-add").forEach((button) => {
        button.removeEventListener(
          "mouseenter",
          this.quickAddButtonMouseEnterHandler
        );
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = "true";
        button.addEventListener(
          "mouseenter",
          this.quickAddButtonMouseEnterHandler
        );
      }
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: fetch(evt.target.dataset.productUrl),
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles `click` events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches(".js-quick-add")) return;

      // find the drawer that belongs to this card
      const drawer = QuickAddDrawer.drawerFor(evt.target);

      // if a cart‐drawer is open, close it first, then re‑open our drawer
      if (drawer.cartDrawer && drawer.cartDrawer.ariaHidden === "false") {
        const overlay = document.querySelector(".js-overlay.is-visible");
        if (overlay) overlay.style.transitionDelay = "200ms";

        drawer.cartDrawer.close();
        setTimeout(() => {
          drawer.backBtn.hidden = false;
          drawer.open(evt.target);
          if (overlay) overlay.style.transitionDelay = "";
        }, 200);
      } else {
        drawer.backBtn.hidden = false;
        drawer.open(evt.target);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split("?").length > 1 ? "&" : "?";
        url = `${separator}variant=${evt.detail.variant.id}`;

        /* NEW — remember the currently-picked variant so “No selection”
                   or cross-sell logic always adds the right one                */
        this.dataset.variantId = evt.detail.variant.id;
      }

      this.querySelectorAll(".js-prod-link").forEach((link) => {
        link.href = url;
      });
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute("aria-disabled", "true");

      if (this.notification) this.notification.hidden = true;

      // If it's the same product as previously shown, re‑open and re‑render
      if (this.productUrl === opener.dataset.productUrl) {
        super.open(opener);
        opener.removeAttribute("aria-disabled");

        // re‑inject content & form in case it was cleared
        this.updateContent();
        this.updateForm();

        // re‑draw the media
        const activeMedia = this.productEl.querySelector(
          ".media-viewer__item.is-current-variant"
        );
        if (activeMedia) this.updateMedia(activeMedia.dataset.mediaId);

        return;
      }

      // otherwise, fetch & render anew
      this.productUrl = opener.dataset.productUrl;
      this.content.innerHTML = "";
      this.classList.add("is-loading");
      this.content.classList.add("drawer__content--out");
      this.footer.classList.add("drawer__footer--out");

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl),
        };
      }

      const response = await this.fetch.promise;
      if (response.ok) {
        const tmpl = document.createElement("template");
        tmpl.innerHTML = await response.text();
        this.productEl = tmpl.content.querySelector(
          ".cc-main-product .js-product"
        );
        this.renderProduct(opener);
      }

      this.fetch = null;
      opener.removeAttribute("aria-disabled");
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });

      this.querySelectorAll("qa-upsell").forEach((upsellEl) => {
        if (typeof upsellEl.reset === "function") upsellEl.reset();
      });

      this.querySelectorAll("qa-cross-sell").forEach((csEl) => {
        if (typeof csEl.reset === "function") csEl.reset();
      });
    }

    /**
     * Renders the product details after fetch.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      // Replace section IDs to avoid duplicates
      const sectionId = this.productEl.dataset.section;
      this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(
        sectionId,
        "quickadd"
      );

      // Prevent the variant picker from changing the URL
      const variantPicker = this.productEl.querySelector("variant-picker");
      if (variantPicker) {
        variantPicker.dataset.updateUrl = "false";
        this.selectFirstVariant =
          variantPicker.dataset.selectFirstVariant === "true";
      }

      // Remove size‑chart modal if present
      const sizeChartModal = this.productEl.querySelector(
        '[data-modal="size-chart"]'
      );
      if (sizeChartModal) sizeChartModal.remove();

      // Build the rest of the drawer content & form
      this.updateContent();
      this.updateForm();

      const idInput = this.querySelector('input[name="id"]');
      if (idInput) {
        // store the PDP variant id so "No" will re‑add exactly that
        this.dataset.variantId = idInput.value;
      }

      // ── now inject upsell / cross‑sell ──
      const upsellData = QuickAddDrawer.safeJson(opener.dataset.upsell);
      const crossData = QuickAddDrawer.safeJson(opener.dataset.crosssell);
      const sellWrap = this.querySelector(".qa-sell-wrapper");

      if (upsellData) {
        sellWrap.innerHTML = window.quickAddUpsellTpl(upsellData, opener);
        sellWrap.firstElementChild?.setAttribute("is", "qa-upsell");
      } else if (Array.isArray(crossData) && crossData.length) {
        sellWrap.innerHTML = window.quickAddCrossTpl(crossData, opener);
        sellWrap.firstElementChild?.setAttribute("is", "qa-cross-sell");
      } else {
        sellWrap.innerHTML = "";
      }

      // hide footer ATC if any upsell/cross-sell got injected
      this.footer.classList.toggle("hidden", sellWrap.children.length > 0);

      // DEFER media update until after layout so container width is correct
      requestAnimationFrame(() => {
        const activeMedia = this.productEl.querySelector(
          ".media-viewer__item.is-current-variant"
        );
        if (activeMedia) {
          this.updateMedia(activeMedia.dataset.mediaId);
        }
      });

      // Restore any pre‑selected color variant
      if (opener.dataset.selectedColor && this.selectFirstVariant) {
        setTimeout(() => this.setActiveVariant(opener), 10);
      }
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(
        `.opt-btn[value="${opener.dataset.selectedColor}"]`
      );
      if (colorOptionBox) {
        this.querySelector(
          `.opt-btn[value="${opener.dataset.selectedColor}"]`
        ).click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest("custom-select");
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} mediaId - Id of the media item to show.
     */
    updateMedia(mediaId) {
      const img = this.productEl.querySelector(
        `[data-media-id="${mediaId}"] img`
      );
      if (!img) return;

      const src = img.src
        ? img.src.split("&width=")[0]
        : img.dataset.src.split("&width=")[0];
      const container = this.querySelector(".quick-add-info__media");
      const width = container.offsetWidth;
      const aspectRatio = img.width / img.height;

      container.innerHTML = `
        <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${
        width * 2
      } 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${
        img.alt
      }">
      `;
    }

    /**
     * Builds the markup for the drawer content element.
     */
    updateContent() {
      let weightElem = this.getElementHtml(".product-info__weight");
      if (weightElem && weightElem.length > 0) {
        weightElem = `<div class="product-info__weight text-sm mt-2">${weightElem}</div>`;
      }

      this.content.innerHTML = `
        <div class="quick-add-info grid mb-4">
          <div class="quick-add-info__media${
            theme.settings.blendProductImages ? " image-blend" : ""
          }"></div>
          <div class="quick-add-info__details">
            <div class="product-title">
              <a class="h6 js-prod-link" href="${this.productUrl}">
                ${this.getProductTitle()}
              </a>
            </div>
            ${weightElem}
            <div class="product-price">
              ${this.getFirstElementHtml(".product-price")}
            </div>
            <div class="text-theme-light text-sm mt-2 details-button">
              <a href="${this.productUrl}" class="link js-prod-link">
                ${theme.strings.viewDetails}
              </a>
            </div>
          </div>
          <div class="quick-add-info__close">
            <button type="button" class="drawer__close-btn quick-add__close-btn--grid js-close-drawer">
              <span class="visually-hidden">${
                theme.strings.accessibility
                  ? theme.strings.accessibility.close
                  : "Close"
              }</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" style="pointer-events: none;">
                <path d="M5 19L19 5M5 5L19 19" stroke="#262325" stroke-width="1.5" stroke-linejoin="round" style="pointer-events: none;"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="product-options">
          ${this.getFirstElementHtml(".product-options")}
        </div>
        <div class="product-backorder">
          ${this.getFirstElementHtml(".product-backorder")}
        </div>
      `;

      this.classList.remove("is-loading");
      this.content.classList.remove("drawer__content--out");

      // Ensure the new close button works
      const closeBtn = this.content.querySelector(
        ".quick-add__close-btn--grid"
      );
      if (closeBtn) {
        closeBtn.addEventListener("click", async () => {
          // Check if this drawer contains an upsell - only add to cart in that case
          const hasUpsell = this.querySelector("qa-upsell") !== null;

          if (hasUpsell) {
            // Add the current product to cart before closing (upsell behavior)
            const variantId = this.getCurrentVariantId();
            const quantity = this.getCurrentQuantity();

            if (variantId) {
              try {
                await fetch("/cart/add.js", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    items: [{ id: variantId, quantity: quantity }],
                  }),
                });
                document.dispatchEvent(
                  new CustomEvent("dispatch:cart-drawer:refresh")
                );
              } catch (error) {
                console.error("[QuickAddDrawer] Error adding product:", error);
              }
            }
          }

          this.close();
          if (hasUpsell) {
            document.dispatchEvent(
              new CustomEvent("dispatch:cart-drawer:open")
            );
          }
        });
      }
    }

    /**
     * Retrieves the current variant ID from the main picker, form, or dataset.
     * @returns {string} The current variant ID.
     */
    getCurrentVariantId() {
      // First check the main variant picker (most reliable)
      const mainPicker = this.content.querySelector(
        ".product-options variant-picker"
      );
      if (mainPicker && mainPicker.variant && mainPicker.variant.id) {
        return mainPicker.variant.id;
      }

      // Then check the form's hidden input
      const variantInput = this.form.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        return variantInput.value;
      }

      // Finally, fallback to the dataset variant ID (from variant changes)
      if (this.dataset.variantId) {
        return this.dataset.variantId;
      }

      return "";
    }

    /**
     * Retrieves the current quantity from the quantity input.
     * @returns {string|number} The quantity value.
     */
    getCurrentQuantity() {
      const qtyInput = this.form.querySelector(".qty-input__input");
      return qtyInput ? qtyInput.value : 1;
    }

    /**
     * Handles a 'click' event on the drawer.
     * @param {object} evt - Event object.
     */
    async handleClick(evt) {
      if (
        evt.target.matches(".js-close-drawer") ||
        evt.target === this.overlay
      ) {
        // Check if this drawer contains an upsell - only add to cart in that case
        const hasUpsell = this.querySelector("qa-upsell") !== null;

        if (hasUpsell) {
          // Add the current product to cart before closing (upsell behavior)
          const variantId = this.getCurrentVariantId();
          const quantity = this.getCurrentQuantity();

          if (variantId) {
            try {
              await fetch("/cart/add.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  items: [{ id: variantId, quantity: quantity }],
                }),
              });
              document.dispatchEvent(
                new CustomEvent("dispatch:cart-drawer:refresh")
              );
            } catch (error) {
              console.error("[QuickAddDrawer] Error adding product:", error);
            }
          }
        }

        this.close();
        if (hasUpsell) {
          document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open"));
        }
      } else {
        // Call parent method for other clicks
        super.handleClick(evt);
      }
    }

    /**
     * Updates the Quick Add drawer form (buy buttons).
     */
    updateForm() {
      const productForm = this.productEl.querySelector("product-form");
      this.footer.classList.remove("quick-add__footer-message");

      if (productForm) {
        this.form.innerHTML = productForm.innerHTML;

        // In the quick-add drawer we don't use PDP popups.
        // Ensure the ATC button actually submits the form here.
        const addButton = this.form.querySelector('[name="add"]');
        if (addButton) {
          addButton.type = "submit";
          addButton.classList.remove("js-cross-sell-trigger");
          addButton.removeAttribute("data-has-cross-sell");
          addButton.removeAttribute("data-has-upsell");
        }

        // Ensure the hidden variant input reflects the user's current selection
        // even if the variant-picker initialized before the form was injected.
        const nativeForm = this.form.querySelector(".js-product-form");
        const idInput =
          nativeForm && nativeForm.querySelector('input[name="id"]');
        const picker = this.content.querySelector("variant-picker");
        if (nativeForm && idInput) {
          nativeForm.addEventListener(
            "submit",
            () => {
              try {
                if (picker && picker.variant && picker.variant.id) {
                  idInput.value = picker.variant.id;
                }
              } catch (e) {
                // no-op
              }
            },
            true
          );
        }

        // Now initialize the product-form element so AJAX submission works.
        this.form.init();

        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } else {
        const signUpForm = this.productEl.querySelector(".product-signup");
        if (signUpForm) {
          this.form.innerHTML = signUpForm.innerHTML;
        } else {
          this.footer.classList.add("quick-add__footer-message");
          this.form.innerHTML = `
            <div class="alert quick-add__alert bg-info-bg text-info-text">
              <div class="flex">
                <div>
                  <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                    <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                    <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                  <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
                </div>
              </div>
            </div>`;
        }
      }

      this.footer.classList.remove("drawer__footer--out");
    }

    /**
     * Gets the innerHTML of elements within the product element.
     * @param {string} selector - CSS selector for the element.
     * @param {...*} additionalSelectors - Additional CSS selectors for elements.
     * @returns {?string}
     */
    getElementHtml(selector, ...additionalSelectors) {
      const selectors = [selector].concat(additionalSelectors);
      let html = "";
      selectors.forEach((sel) => {
        const els = this.productEl.querySelectorAll(sel);
        Array.from(els).forEach((el) => {
          html += el.innerHTML;
        });
      });
      return html;
    }

    /**
     * Gets the innerHTML of the first matching element within the product element.
     * @param {string} selector - CSS selector for the element.
     * @returns {?string}
     */
    getFirstElementHtml(selector) {
      const el = this.productEl.querySelector(selector);
      return el ? el.innerHTML : "";
    }

    /**
     * Gets the product title text content without HTML tags.
     * @returns {string}
     */
    getProductTitle() {
      const titleEl = this.productEl.querySelector(".product-title");
      if (!titleEl) return "";

      // If there's an anchor tag inside, get its text content
      const anchorEl = titleEl.querySelector("a");
      if (anchorEl) {
        return anchorEl.textContent.trim();
      }

      // Otherwise get the text content of the title element itself
      return titleEl.textContent.trim();
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }
  }

  customElements.define("quick-add-drawer", QuickAddDrawer);
}
